import React, { useState, useEffect } from 'react';
import CaseTimeline from './CaseTimeline';
import CourtApplication from './CourtApplication';
import SittingTenantRisk from './SittingTenantRisk';
import MarkAsServedModal from './MarkAsServedModal';
import { evictionService, legalService, tenantService } from '../../services/api';

interface CaseDetailProps {
    caseData: any;
    onUpdate: () => void;
    onBack: () => void;
}

export default function CaseDetail({ caseData, onUpdate, onBack }: CaseDetailProps) {
    const [submitting, setSubmitting] = useState(false);
    const [note, setNote] = useState('');
    const [showNoteInput, setShowNoteInput] = useState(false);
    const [pendingAction, setPendingAction] = useState('');
    const [showNoticeForm, setShowNoticeForm] = useState(false);
    const [rentArrears, setRentArrears] = useState('0');
    const [rentPeriod, setRentPeriod] = useState('weekly');
    const [showMarkServed, setShowMarkServed] = useState(false);
    const [noticeDateInfo, setNoticeDateInfo] = useState<any>(null);
    const [calculatingDate, setCalculatingDate] = useState(false);

    const handleAction = async (action: string, nextStatus?: string) => {
        if (!window.confirm('Are you sure you want to proceed with this action?')) return;

        setSubmitting(true);
        try {
            await evictionService.updateStep(caseData.id, {
                action,
                nextStatus,
                note: note || `User performed action: ${action}`
            });
            setNote('');
            setShowNoteInput(false);
            setPendingAction('');
            onUpdate(); // Refresh parent
        } catch (e) {
            alert('Failed to update case');
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleIssueNotice = async () => {
        if (!window.confirm('Issue eviction notice? This will generate and serve the formal notice document.')) return;

        setSubmitting(true);
        try {
            const res = await evictionService.issueNotice(caseData.id, {
                rentArrearsAmount: parseFloat(rentArrears),
                rentPeriod
            });

            if (res.data?.pdfBase64) {
                // Download PDF
                const link = document.createElement('a');
                link.href = `data:application/pdf;base64,${res.data.pdfBase64}`;
                link.download = `Notice_${caseData.id}.pdf`;
                link.click();
            }

            setShowNoticeForm(false);
            setRentArrears('0');
            onUpdate();
        } catch (e) {
            alert('Failed to issue notice');
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
      if (caseData?.tenantId && caseData?.groundCode) {
        setCalculatingDate(true);
        (async () => {
          try {
            const tenantRes = await tenantService.getTenant(caseData.tenantId);
            const tenant = tenantRes.data?.data || tenantRes.data;
            const rpd = tenant?.rentPaymentDay;
            if (rpd) {
              const calcRes = await legalService.calculateNoticeDate({
                rentPaymentDay: rpd,
                tenancyStartDate: tenant.tenancyStartDate,
                groundType: caseData.groundCode,
                freePeriodDays: tenant.freePeriodDays || 0,
                freePeriodGranted: tenant.freePeriodGranted || false,
              });
              setNoticeDateInfo(calcRes.data?.data || calcRes.data);
            }
          } catch (e) {
            console.warn('Notice date calculation not available:', e);
          } finally {
            setCalculatingDate(false);
          }
        })();
      }
    }, [caseData?.tenantId, caseData?.groundCode]);

    const getStatusColor = (status: string) => {
        if (!status) return 'bg-gray-100 text-gray-800';
        switch (status) {
            case 'draft': return 'bg-gray-100 text-gray-800';
            case 'notice_issued': return 'bg-blue-100 text-blue-800';
            case 'notice_served': return 'bg-yellow-100 text-yellow-800';
            case 'court': return 'bg-orange-100 text-orange-800';
            case 'completed': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200">
                <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {typeof caseData?.tenantName === 'object' ? 'Tenant' : (caseData?.tenantName || 'Tenant')} - Eviction Case
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="max-w-2xl text-sm text-gray-500">
                            Ground: {typeof caseData?.groundCode === 'object' ? (caseData?.groundCode.code || caseData?.groundCode.name) : (caseData?.groundCode || 'N/A')} • Created: {caseData?.createdAt ? new Date(caseData.createdAt).toLocaleDateString() : 'N/A'}
                        </p>
                        <button
                            onClick={async () => {
                                try {
                                    const res = await evictionService.getCaseBundle(caseData.id);
                                    if (res.data?.success && res.data?.pdfBase64) {
                                        const link = document.createElement('a');
                                        link.href = `data:application/pdf;base64,${res.data.pdfBase64}`;
                                        link.download = `Case_Bundle_${caseData.id}.pdf`;
                                        link.click();
                                    }
                                } catch (e) {
                                    console.error("Bundle failed", e);
                                    alert("Bundle generation failed. Please check compliance status.");
                                }
                            }}
                            className="ml-4 inline-flex items-center px-3 py-1 border border-indigo-600 text-xs font-bold text-indigo-600 rounded hover:bg-indigo-50 transition-colors"
                        >
                            <i className="fas fa-file-pdf mr-2"></i> DOWNLOAD CASE BUNDLE
                        </button>
                    </div>
                </div>
                <button
                    onClick={onBack}
                    className="text-sm text-gray-500 hover:text-gray-700"
                >
                    <i className="fas fa-arrow-left mr-1"></i> Back to List
                </button>
            </div>

            <div className="px-4 py-5 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Details & Actions */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Status Card */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-500">Current Status</span>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full uppercase ${getStatusColor(caseData?.status)}`}>
                                {(caseData?.status || 'Draft').replace('_', ' ')}
                            </span>
                        </div>

                        {noticeDateInfo && (
                          <div className={`mt-4 p-3 rounded-lg border ${
                            noticeDateInfo.isValid
                              ? 'border-green-200 bg-green-50'
                              : 'border-red-200 bg-red-50'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className={`text-sm font-medium ${
                                  noticeDateInfo.isValid ? 'text-green-900' : 'text-red-900'
                                }`}>
                                  {noticeDateInfo.isValid ? 'Notice Date Valid' : 'Notice Date Invalid'}
                                </p>
                                <p className="text-xs mt-1 text-gray-600">
                                  Earliest valid service date:{' '}
                                  <strong>{new Date(noticeDateInfo.earliestValidDate).toLocaleDateString()}</strong>
                                </p>
                                <p className="text-xs text-gray-500">
                                  Notice period: {noticeDateInfo.noticePeriodLabel}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  <i className="fas fa-book mr-1"></i>
                                  {noticeDateInfo.citation}
                                </p>
                              </div>
                              <i className={`fas ${noticeDateInfo.isValid ? 'fa-check-circle text-green-600' : 'fa-exclamation-triangle text-red-600'} text-xl`}></i>
                            </div>
                            {noticeDateInfo.warnings?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {noticeDateInfo.warnings.map((w: any, i: number) => (
                                  <p key={i} className="text-xs text-amber-700">
                                    <i className="fas fa-exclamation-circle mr-1"></i>
                                    {w.message}
                                  </p>
                                ))}
                              </div>
                            )}
                            {!noticeDateInfo.isValid && (
                              <p className="text-xs text-red-600 mt-2">
                                Generating a notice with an invalid date may result in an unenforceable notice.
                              </p>
                            )}
                          </div>
                        )}
                        {calculatingDate && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500">
                              <i className="fas fa-spinner fa-spin mr-2"></i>Calculating notice date...
                            </p>
                          </div>
                        )}
                        <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Available Actions</h4>
                            <div className="flex flex-wrap gap-2">
                                {caseData.status === 'draft' && (
                                    <button
                                        onClick={() => setShowNoticeForm(true)}
                                        disabled={submitting}
                                        className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                                    >
                                        <i className="fas fa-file-pdf mr-2"></i>Issue & Serve Notice
                                    </button>
                                )}
                                {caseData.status === 'notice_issued' && (
                                    <>
                                        <button
                                            onClick={() => setShowMarkServed(true)}
                                            disabled={submitting}
                                            className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                        >
                                            <i className="fas fa-check-circle mr-2"></i>Mark as Served
                                        </button>
                                        <button
                                            onClick={() => handleAction('COURT_APPLICATION', 'court')}
                                            disabled={submitting}
                                            className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                                        >
                                            File Court Application
                                        </button>
                                    </>
                                )}
                                {caseData.status === 'notice_served' && (
                                    <>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const res = await evictionService.getCertificateOfService(caseData.id);
                                                    if (res.data?.data?.pdfBase64) {
                                                        const link = document.createElement('a');
                                                        link.href = `data:application/pdf;base64,${res.data.data.pdfBase64}`;
                                                        link.download = `Certificate_of_Service_${caseData.id}.pdf`;
                                                        link.click();
                                                    }
                                                } catch (e) {
                                                    console.error('Certificate download failed', e);
                                                    alert('Certificate of Service not found. Please ensure the notice was served.');
                                                }
                                            }}
                                            className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                        >
                                            <i className="fas fa-file-pdf mr-2"></i>Download Certificate of Service
                                        </button>
                                        <button
                                            onClick={() => handleAction('COURT_APPLICATION', 'court')}
                                            disabled={submitting}
                                            className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                                        >
                                            File Court Application
                                        </button>
                                    </>
                                )}
                                {caseData.status === 'court' && (
                                    <button
                                        onClick={() => {
                                            setShowNoteInput(true);
                                            setPendingAction('OUTCOME');
                                        }}
                                        disabled={submitting}
                                        className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                    >
                                        Record Outcome
                                    </button>
                                )}
                                {caseData.status === 'completed' && (
                                    <span className="text-sm text-gray-500 italic">Case Closed</span>
                                )}
                            </div>
                        </div>

                        {/* Issue Notice Form - Ground 8 Specific */}
                        {showNoticeForm && (
                            <div className="mt-4 p-4 border border-blue-200 bg-blue-50 rounded-lg">
                                <h4 className="text-sm font-bold text-gray-900 mb-3">Issue Eviction Notice (Ground 8 - Serious Rent Arrears)</h4>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Total Rent Arrears (£)</label>
                                        <input
                                            type="number"
                                            value={rentArrears}
                                            onChange={(e) => setRentArrears(e.target.value)}
                                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            placeholder="0.00"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Rent Payment Period</label>
                                        <select
                                            value={rentPeriod}
                                            onChange={(e) => setRentPeriod(e.target.value)}
                                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        >
                                            <option value="weekly">Weekly</option>
                                            <option value="fortnightly">Fortnightly</option>
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                        </select>
                                    </div>
                                    <div className="flex space-x-2 pt-2">
                                        <button
                                            onClick={handleIssueNotice}
                                            disabled={submitting || !rentArrears}
                                            className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            {submitting ? 'Issuing...' : 'Generate & Issue Notice'}
                                        </button>
                                        <button
                                            onClick={() => setShowNoticeForm(false)}
                                            className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Notice Issued Summary */}
                        {caseData.noticeIssuedDate && (
                            <div className="mt-4 p-3 border border-green-200 bg-green-50 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-green-900">✓ Notice Issued & Served</p>
                                        <p className="text-xs text-green-700 mt-1">
                                            Issued: {new Date(caseData.noticeIssuedDate).toLocaleDateString()}
                                            {caseData.rentArrearsAmount && ` • Arrears: £${caseData.rentArrearsAmount.toFixed(2)}`}
                                        </p>
                                    </div>
                                    {caseData.pdfGenerated && (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const res = await evictionService.getCaseBundle(caseData.id);
                                                    if (res.data?.pdfBase64) {
                                                        const link = document.createElement('a');
                                                        link.href = `data:application/pdf;base64,${res.data.pdfBase64}`;
                                                        link.download = `Notice_${caseData.id}.pdf`;
                                                        link.click();
                                                    }
                                                } catch (e) {
                                                    console.error("Download failed", e);
                                                }
                                            }}
                                            className="text-sm text-green-700 hover:text-green-900 font-medium"
                                        >
                                            <i className="fas fa-download mr-1"></i>Download Notice
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {showNoteInput && (
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700">Outcome Note</label>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    rows={3}
                                />
                                <div className="mt-2 flex space-x-2">
                                    <button
                                        onClick={() => handleAction(pendingAction || 'UPDATE', 'completed')}
                                        className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md"
                                    >
                                        Save
                                    </button>
                                    <button
                                        onClick={() => setShowNoteInput(false)}
                                        className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* F-023: Sitting Tenant Risk Mitigation */}
                    {['notice_served', 'court'].includes(caseData.status) && (
                        <SittingTenantRisk caseData={caseData} onUpdate={onUpdate} />
                    )}

                    {/* Court Application Details - FEATURE F-022 */}
                    {caseData.status === 'court' && (
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Court & Enforcement Portal</h4>
                            <CourtApplication caseData={caseData} onUpdate={onUpdate} />
                        </div>
                    )}

                    {/* Notice Preview */}
                    {caseData.noticeHtml && (
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Original Generated Notice</h4>
                            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto">
                                <div dangerouslySetInnerHTML={{ __html: caseData.noticeHtml }} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column: Timeline */}
                <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Case Timeline</h4>
                    <CaseTimeline steps={Array.isArray(caseData?.steps) ? caseData.steps : []} />
                </div>
            </div>

            {showMarkServed && (
                <MarkAsServedModal
                    caseData={caseData}
                    onClose={() => setShowMarkServed(false)}
                    onComplete={() => {
                        setShowMarkServed(false);
                        onUpdate();
                    }}
                />
            )}
        </div>
    );
}

